import { useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as doorsApi from "../../api/doors.api.js";
import { DataTable } from "../../components/DataTable.js";
import { RefreshIcon } from "../../components/Icon.js";
import { Pager } from "../../components/Pager.js";
import { FilterBar, FilterDate, FilterSelect, dateParam } from "../../components/FilterBar.js";
import { useAuth } from "../../lib/auth-context.js";
import { formatTimestamp, severityIcon, severityTone } from "../../lib/format.js";
import { AlertDetail } from "../../components/AlertDetail.js";
import { STATUS_POLL_MS, refetchIntervalFor } from "../../lib/polling.js";
import type { AlertFilters, Severity } from "../../types/api.js";

const SEVERITIES = [
  { value: "critical", label: "critical" },
  { value: "warning", label: "warning" },
  { value: "info", label: "info" },
] as const;

const ACK_STATES = [
  { value: "false", label: "not yet acknowledged" },
  { value: "true", label: "acknowledged" },
] as const;

interface FilterState {
  severity: string;
  acknowledged: string;
  from: string;
  to: string;
}

const EMPTY: FilterState = { severity: "", acknowledged: "", from: "", to: "" };

export function AlertsPage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const [filters, setFilters] = useState<FilterState>(EMPTY);
  const [limit, setLimit] = useState(50);
  const [offset, setOffset] = useState(0);
  const [ackError, setAckError] = useState<string | null>(null);

  const query: AlertFilters = {
    ...(filters.severity ? { severity: filters.severity as Severity } : {}),
    ...(filters.acknowledged ? { acknowledged: filters.acknowledged === "true" } : {}),
    ...(dateParam(filters.from) ? { from: dateParam(filters.from) } : {}),
    ...(dateParam(filters.to, true) ? { to: dateParam(filters.to, true) } : {}),
  };

  const alerts = useQuery({
    queryKey: ["alerts", query, limit, offset],
    queryFn: () => doorsApi.listAlerts({ ...query, limit, offset }),
    refetchInterval: refetchIntervalFor(STATUS_POLL_MS),
    placeholderData: keepPreviousData,
  });

  // The unacknowledged-critical count comes from /status, which counts across the WHOLE table.
  // Counting the rows on this page would under-report the moment anyone paged or filtered, and
  // "how many criticals are outstanding" is not a question to answer with whatever happens to
  // be on screen.
  const status = useQuery({
    queryKey: ["status"],
    queryFn: doorsApi.serviceStatus,
    refetchInterval: refetchIntervalFor(STATUS_POLL_MS),
  });

  const acknowledge = useMutation({
    mutationFn: doorsApi.acknowledgeAlert,
    onMutate: () => setAckError(null),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["alerts"] });
      void queryClient.invalidateQueries({ queryKey: ["status"] });
    },
    onError: (error: Error) => setAckError(error.message),
  });

  const list = alerts.data?.items ?? [];
  const page = alerts.data?.page;
  const criticalOutstanding = status.data?.alerts.critical ?? 0;
  const activeFilters = Object.values(filters).filter(Boolean).length;

  // Acknowledging is a SECURITY action on the server (it clears a "this door may be held
  // unlocked" from the outstanding counts), so it takes manage_security. Hiding the button for
  // a role that cannot use it is a courtesy; the API is what actually refuses it.
  const canAcknowledge = can("manage_security");

  function update<K extends keyof FilterState>(key: K, value: FilterState[K]) {
    setFilters((current) => ({ ...current, [key]: value }));
    setOffset(0);
  }

  return (
    <>
      <div className="page-head">
        <h1 id="page-heading" tabIndex={-1}>
          Alerts
        </h1>
        <button
          type="button"
          className="btn"
          onClick={() => void alerts.refetch()}
          disabled={alerts.isFetching}
        >
          <RefreshIcon aria-hidden="true" focusable="false" />
          {alerts.isFetching ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      <p className="page-intro">
        Everything the service decided was worth telling somebody about. Acknowledging an alert
        records that a person has seen it; it does not change anything about the door.
      </p>

      {criticalOutstanding > 0 ? (
        <div className="banner banner-critical" role="alert">
          <span className="banner-icon" aria-hidden="true">
            ✗
          </span>
          <div className="banner-body">
            <strong>
              {criticalOutstanding} unacknowledged critical alert
              {criticalOutstanding === 1 ? "" : "s"}.
            </strong>{" "}
            A critical alert means a door may be physically held unlocked. Check the door, not just
            this screen.
          </div>
        </div>
      ) : null}

      {ackError ? (
        <div className="banner banner-critical" role="alert">
          <span className="banner-icon" aria-hidden="true">
            ✗
          </span>
          <div className="banner-body">{ackError}</div>
        </div>
      ) : null}

      {!canAcknowledge ? (
        <div className="banner banner-ok">
          <span className="banner-icon" aria-hidden="true">
            ·
          </span>
          <div className="banner-body">
            Your role can read alerts but not acknowledge them — clearing a &ldquo;this door may be
            held unlocked&rdquo; alert is a security decision, so it needs a security role.
          </div>
        </div>
      ) : null}

      <DataTable
        caption="Alerts raised by the service, newest first"
        isLoading={alerts.isLoading}
        isError={alerts.isError}
        isEmpty={list.length === 0}
        loadingMessage="Loading alerts…"
        errorMessage="The alerts could not be loaded. This is a display problem — an alert that was raised has still been recorded, and the sinks have already delivered it."
        emptyMessage={
          activeFilters > 0 ? "No alerts match these filters." : "No alerts have been raised."
        }
        toolbar={
          <FilterBar
            activeCount={activeFilters}
            onClear={() => {
              setFilters(EMPTY);
              setOffset(0);
            }}
          >
            <FilterSelect
              label="Severity"
              value={filters.severity}
              anyLabel="Any severity"
              options={SEVERITIES.map((s) => ({ value: s.value as string, label: s.label }))}
              onChange={(value) => update("severity", value)}
            />
            <FilterSelect
              label="State"
              value={filters.acknowledged}
              anyLabel="Any state"
              options={ACK_STATES.map((a) => ({ value: a.value as string, label: a.label }))}
              onChange={(value) => update("acknowledged", value)}
            />
            <FilterDate label="From" value={filters.from} onChange={(v) => update("from", v)} />
            <FilterDate label="To" value={filters.to} endOfDay onChange={(v) => update("to", v)} />
          </FilterBar>
        }
        columns={
          <>
            <th scope="col">Raised</th>
            <th scope="col">Severity</th>
            <th scope="col">Alert</th>
            <th scope="col">Detail</th>
            <th scope="col">Acknowledged</th>
          </>
        }
        footer={
          page ? (
            <Pager
              total={page.total}
              limit={page.limit}
              offset={page.offset}
              hasMore={page.hasMore}
              count={list.length}
              busy={alerts.isFetching}
              onOffsetChange={setOffset}
              onLimitChange={setLimit}
            />
          ) : null
        }
      >
        {list.map((alert) => (
          <tr key={alert.id}>
            <td className="cell-sub">{formatTimestamp(alert.raisedAt)}</td>
            <td>
              <span className={`status tone-${severityTone(alert.severity)}`}>
                <span className="status-icon" aria-hidden="true">
                  {severityIcon(alert.severity)}
                </span>
                <span>{alert.severity}</span>
              </span>
            </td>
            <td>
              <div className="cell-name">{alert.summary}</div>
              <div className="cell-sub mono">{alert.key}</div>
            </td>
            <td>
              <AlertDetail detail={alert.detail} />
            </td>
            <td>
              {alert.acknowledgedAt ? (
                <>
                  <div className="cell-sub">{formatTimestamp(alert.acknowledgedAt)}</div>
                  {alert.acknowledgedByName ? (
                    <div className="cell-sub">by {alert.acknowledgedByName}</div>
                  ) : null}
                </>
              ) : canAcknowledge ? (
                <button
                  type="button"
                  className="btn"
                  disabled={acknowledge.isPending}
                  onClick={() => acknowledge.mutate(alert.id)}
                >
                  Acknowledge
                </button>
              ) : (
                <span className="cell-sub">not yet acknowledged</span>
              )}
            </td>
          </tr>
        ))}
      </DataTable>
    </>
  );
}
